import { useEffect, useRef, useState } from "react";
import { previewAiApplyPast, runAiApplyPast, getAiApplyPastStatus } from "../../../api/challenge";

const RED = '#b91c1c';
const GREEN = '#25a55f';
const sw = (s) => s ? `${s.enabled ? 'ON' : 'OFF'} (${s.threshold}%)` : '';
const errText = (e, fallback) => e?.response?.data?.error || fallback;

// Applies the current AI settings to the PENDING submissions in the current search results.
// Uses the AI reviews already done, so there is no AI cost.
const AiApplyPast = ({ filters, filterLabel, onSearch, onDone }) => {
    const [preview, setPreview] = useState(null);
    const [run, setRun] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const timer = useRef(null);
    const key = JSON.stringify(filters);

    const poll = async () => {
        try {
            const r = await getAiApplyPastStatus();
            setRun(r);
            if (r && r.status === 'running') timer.current = setTimeout(poll, 3000);
            else if (r && onDone) onDone();
        } catch (e) { setError(errText(e, "Could not read the progress.")); }
    };

    useEffect(() => {
        getAiApplyPastStatus().then((r) => { if (r && r.status === 'running') { setRun(r); timer.current = setTimeout(poll, 3000); } }).catch(() => {});
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Search changed: the old numbers no longer apply.
    useEffect(() => { setPreview(null); }, [key]);

    const check = async () => {
        setBusy(true); setError("");
        if (onSearch) onSearch(); // the list below shows the same search the numbers are for
        try { setPreview(await previewAiApplyPast(filters)); }
        catch (e) { setError(errText(e, "Could not check these submissions.")); }
        finally { setBusy(false); }
    };

    const apply = async () => {
        const c = preview.counts;
        const ok = window.confirm(`This will approve ${c.approve} and reject ${c.reject} submission(s) now, using your current AI settings. Users will be notified. This cannot be undone. Continue?`);
        if (!ok) return;
        setBusy(true); setError("");
        try {
            const r = await runAiApplyPast(filters);
            setRun(r); setPreview(null);
            timer.current = setTimeout(poll, 2000);
        } catch (e) { setError(errText(e, "Could not start.")); }
        finally { setBusy(false); }
    };

    const c = preview?.counts;
    const toAct = c ? c.approve + c.reject : 0;
    const isRunning = run?.status === 'running';

    return (
        <div className="mt-4 px-4 py-3 rounded-md border border-[#e0a800] bg-[#fff8e1] text-gray-900">
            <p className="font-bold">AI review: apply to past submissions</p>
            <p className="text-sm mt-1">Applies your current AI settings to the <b>pending</b> submissions in the search results ({filterLabel}). It uses the AI reviews already done, so there is no AI cost.</p>
            {!isRunning && (
                <button type="button" onClick={check} disabled={busy}
                    className="mt-2 px-4 py-1.5 rounded text-white font-semibold disabled:opacity-40" style={{ backgroundColor: GREEN }}>
                    {busy && !preview ? 'Checking…' : 'Check these search results'}
                </button>
            )}
            {error && <p className="text-sm mt-2" style={{ color: RED }}>{error}</p>}
            {c && !isRunning && (
                <div className="mt-3 text-sm" data-testid="apply-past-preview">
                    <p>Search results: <b>{c.matched}</b> submissions, of which <b>{c.pending}</b> are pending.</p>
                    <p className="mt-1">With your current settings (Auto Approval {sw(preview.settings.approve)}, Auto Rejection {sw(preview.settings.reject)}):</p>
                    <ul className="list-disc ml-6 mt-1">
                        <li>Will be approved: <b>{c.approve}</b></li>
                        <li>Will be rejected: <b>{c.reject}</b></li>
                        <li>Stay pending for you to decide: <b>{c.stay}</b></li>
                        {c.notReviewedYet > 0 && <li>Not reviewed by the AI yet: <b>{c.notReviewedYet}</b> (the AI reviews new submissions automatically within a few minutes)</li>}
                        {c.olderOrFailed > 0 && <li>Reviewed by an older AI version, or the AI could not review them: <b>{c.olderOrFailed}</b> (these stay with you)</li>}
                    </ul>
                    <button type="button" onClick={apply} disabled={busy || toAct === 0}
                        className="mt-2 px-4 py-1.5 rounded text-white font-semibold disabled:opacity-40" style={{ backgroundColor: toAct ? RED : GREEN }}>
                        {toAct ? `Apply to ${toAct} submission(s)` : 'Nothing to apply'}
                    </button>
                </div>
            )}
            {run && (
                <p className="text-sm mt-3" data-testid="apply-past-run">
                    {isRunning ? 'Running' : run.status === 'finished' ? 'Last run finished' : `Last run ${run.status}`}: {run.done} of {run.total} done. Approved {run.approved}, rejected {run.rejected}{run.failed ? `, could not be processed ${run.failed}` : ''}.
                    {!isRunning && run.finishedAt && <span className="text-xs text-gray-600"> ({new Date(run.finishedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} IST{run.by?.email ? `, by ${run.by.email}` : ''})</span>}
                </p>
            )}
        </div>
    );
};

export default AiApplyPast;
